import React, { useState } from 'react';

import { Table, PageSizeType, Sort } from 'design-system-web';
import { ChargeCode } from 'graphql-query-contracts';

import { useChargeCodeListQuery } from '../helpers/useChargeCodeListQuery';
import { useChargeCodesTableColumns } from '../helpers/useGetChargeCodesTableColumns';
import { SortDirectionEnum } from 'constants-activate';
import { useTranslation } from 'react-i18next';
import { useChannelAdminContext } from 'hooks';
import { useDeleteChargeCodeMutation } from '../helpers/deleteChargeCodeMutation';
import { getDeleteChargeCodeErrorMessage } from '../helpers/getDeleteChargeCodeErrorMessage';
import { legacyColors } from 'constants-colors';

export function ChargeCodesTable() {
  const { t } = useTranslation();
  const { channel } = useChannelAdminContext();
  const [pageSize, setPageSize] = useState<PageSizeType>(25);
  const [page, setPage] = useState(0);

  const [sort, setSort] = useState<Sort>({
    id: 'charge_code',
    direction: SortDirectionEnum.Asc,
  });

  const { data: chargeCodeListQueryResponse, refetch } = useChargeCodeListQuery(
    page,
    pageSize,
    sort,
    ''
  );

  const { deleteChargeCodeMutation } = useDeleteChargeCodeMutation();

  const onDelete = async (chargeCode: ChargeCode) => {
    if (!channel?._id) return;

    try {
      await window.Alert.confirm({
        title: t('abp.productsServices.deleteChargeCode.title', {
          chargeCode: chargeCode.chargeCode,
        }),
        message: t('abp.productsServices.deleteChargeCode.confirmMessage'),
        confirmText: t('abp.productsServices.deleteChargeCode.delete'),
        cancelText: t('abp.productsServices.deleteChargeCode.cancel'),
        confirmButtonStyle: {
          backgroundColor: legacyColors.neutral.red7200,
          borderColor: legacyColors.neutral.red7200,
          color: legacyColors.neutral.white,
        },
      });
    } catch {
      // User cancelled
      return;
    }

    try {
      await deleteChargeCodeMutation(chargeCode.id, channel._id);

      window.Toast.show(
        t('abp.productsServices.deleteChargeCode.success')
      );

      await refetch();
    } catch (error) {
      const errorMessage = getDeleteChargeCodeErrorMessage(
        error,
        t
      );

      try {
        await window.Alert.confirm({
          title: t('abp.productsServices.deleteChargeCode.errorTitle', {
            chargeCode: chargeCode.chargeCode,
          }),
          message: errorMessage,
          confirmText: t('abp.productsServices.deleteChargeCode.gotIt'),
          cancelText: '', // Hide cancel button
          confirmButtonStyle: {
            width: 'auto',
            minWidth: '20%',
          },
        });
      } catch {
        // User dismissed the alert
      }
    }
  };

  const chargeCodesTableColumns = useChargeCodesTableColumns(onDelete);

  return (
    <Table
      columns={chargeCodesTableColumns}
      data={
        chargeCodeListQueryResponse?.accounts?.listChargeCode?.chargeCodes || []
      }
      isLoading={false}
      totalRows={
        chargeCodeListQueryResponse?.accounts?.listChargeCode?.pageInfo?.total
      }
      pageSize={pageSize}
      pagination="server"
      page={page}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      onSortChange={setSort}
    />
  );
}
